import Step from '@/modules/support/model/Step';
import KeyValuePair from '@/modules/core/support/KeyValuePair';
import moment from 'moment';
import Category from '@/modules/support/model/Category';

export default class Guide {
    public id = 0
    public title = ''
    public screen = ''
    public steps: Step[] = []
    public category: Category | null = null
    public generic = false

    public createdAt = new Date()
    public updatedAt = new Date()

    public get isNew () {
        return !this.id
    }

    /**
     * Retorna una lista con los pasos ordenados según la propiedad `order`
     */
    public get orderedSteps () {
        return this.steps.sort((a, b) => {
            if (a.order === b.order) {
                return 0
            }

            return a.order < b.order ? -1 : 1;
        })
    }

    /**
     * Recalcula los ordenes en base a los indices del array
     */
    public calculateOrder (orderFirst = false) {
        if (orderFirst) {
            this.steps = this.orderedSteps
        }

        this.steps.forEach((step, index) => {
            step.order = index + 1
        })

        return this
    }

    /**
     * Añade un pasos y recalcula los ordenes de los demás
     *
     * @param step
     */
    public addStep (step: Step) {
        const index = this.steps.findIndex(s => s.tempId === step.tempId)
        index === -1
            ? this.steps.push(step)
            : this.steps.splice(index, 1, step)

        this.calculateOrder()

        return this
    }

    /**
     * Elimina un paso y recalcula los ordenes de los demás
     *
     * @param step
     */
    public removeStep (step: Step) {
        const index = this.steps.findIndex(s => s.tempId === step.tempId)
        if (index === -1) {
            return
        }

        this.steps.splice(index, 1)
        this.calculateOrder()

        return this
    }

    /**
     * Mueve un paso hacia arriba
     *
     * @param step
     * @param positions
     */
    public moveUp (step: Step, positions = 1) {
        const index = this.steps.findIndex(s => s.tempId === step.tempId)
        if (index === -1) {
            return this
        }

        const newIndex = index - positions
        if (newIndex < 0) {
            return this
        }

        this.steps.splice(index, 1);
        this.steps.splice(newIndex, 0, step);

        this.calculateOrder()

        return this
    }

    /**
     *
     * @param step
     * @param positions
     */
    public moveDown (step: Step, positions = 1) {
        const index = this.steps.findIndex(s => s.tempId === step.tempId)
        if (index === -1) {
            return this
        }

        const newIndex = index + positions
        if (newIndex > this.steps.length - 1) {
            return this
        }

        this.steps.splice(index, 1);
        this.steps.splice(newIndex, 0, step);

        this.calculateOrder()

        return this
    }

    /**
     * Retorna el último orden actual de la lista de pasos
     */
    public get lastPosition () {
        if (!this.steps.length) {
            return 0
        }

        const steps = this.orderedSteps
        const last = steps[steps.length - 1]

        return last.order
    }

    /**
     * Siguiente posición en la lista de pasos
     */
    public get nextPosition () {
        return this.lastPosition + 1
    }

    /**
     * Deserializa la información de la API y crea una entidad
     *
     * @param data
     */
    public static deserialize (data: KeyValuePair) {
        const guide = new this()
        guide.id = data.id
        guide.title = data.title
        guide.screen = data.screen
        guide.createdAt = moment(data.created_at, 'DD-MM-YYYY HH:mm:ss').toDate()
        guide.updatedAt = moment(data.updated_at, 'DD-MM-YYYY HH:mm:ss').toDate()
        guide.generic = data.generic || false

        guide.steps = data.steps.map((step: KeyValuePair) => Step.deserialize(step))
        guide.calculateOrder(true)

        if (data.category) {
            guide.category = Category.deserialize(data.category)
        }

        return guide
    }

    /**
     * Busca un paso de la guía utilizando el ID temporal como campo de búsqueda
     *
     * @param id
     */
    public findStep (id: string): Step | null {
        return this.steps.find(s => s.tempId === id) || null
    }

    /**
     * Serializa la entidad para enviarla en JSOn
     */
    public serialize () {
        return {
            title: this.title,
            screen: this.screen,
            category: this.category && this.category.id ? this.category.id : undefined,
            steps: this.steps.map(s => s.serialize()),
            generic: this.generic,
        }
    }
}
